import {
    BfWorkflowId,
    FaftWorkflowId,
    bfWorkflowIds,
    faftWorkflowIds,
    bfWorkflows,
    faftWorkflows,
} from 'models/workflow';
import { PlanType } from 'models/bfmc';

export type BmcStatusType = 'available' | 'completed' | 'finalised' | 'closed';

interface BmcDisplay {
    logoSrc: string;
    accentColour: string;
    backgroundArt: string | null;
}

const FALLBACK: BmcDisplay = {
    logoSrc: '/thumbnails/bfmc.png',
    accentColour: '#6B6B6B',
    backgroundArt: null,
};

const display = (slug: string, accentColour: string): BmcDisplay => ({
    logoSrc: `/bmc-logos/${slug}.png`,
    accentColour,
    backgroundArt: `/bmc-backgrounds/${slug}.png`,
});

const BMC_DISPLAY: Record<string, BmcDisplay> = {
    AMLR: display('amlr', '#8B9E6F'),
    FL: display('fl', '#6B9DB5'),
    FO: display('fo', '#D4704F'),
    KI: display('ki', '#5A9B9E'),
    LC: display('lc', '#7B8B6F'),
    LEP: display('lep', '#4A6B8A'),
    MNY: display('mny', '#D88B4F'),
    MM: display('mm', '#7B8B6F'),
    UEP: display('uep', '#E8B84D'),
};

export const getBmcDisplay = (code: string | undefined): BmcDisplay =>
    (code && BMC_DISPLAY[code]) || FALLBACK;

export const getReportLogoSrc = (code: string | undefined): string => {
    if (!code || !BMC_DISPLAY[code]) return '/logo-bip.png';
    return `/bmc-logos/${code.toLowerCase()}-sa-gov.png`;
};

export interface WorkshopProgress {
    /** Current stage within the active workshop, 1-N. 0 if not started. */
    stageInWorkshop: number;
    /** Total stages in the active workshop. Varies per workshop
     *  (W1-W3 have 3 stages, W4 has 4 due to `_approved`; FAFT differs). */
    stagesInWorkshop: number;
    /** True for `*_not_started` — UI should show "Not Started" rather
     *  than a stage counter, since the plan hasn't entered any workshop. */
    isNotStarted: boolean;
    statusType: BmcStatusType;
    statusLabel: string;
}

const STATUS_PALETTE: Record<
    BmcStatusType,
    { fg: string; bg: string; border: string }
> = {
    available: { fg: '#166534', bg: '#d1f4dd', border: '#bbf7d0' },
    completed: { fg: '#166534', bg: '#d1f4dd', border: '#bbf7d0' },
    finalised: { fg: 'rgb(93, 31, 52)', bg: '#f3e8ff', border: '#e9d5ff' },
    closed: { fg: '#6B7280', bg: '#f3f4f6', border: '#e5e7eb' },
};

export const getStatusPalette = (type: BmcStatusType) => STATUS_PALETTE[type];

export const getWorkshopProgress = (
    planType: PlanType,
    workshopStatus: BfWorkflowId | FaftWorkflowId,
): WorkshopProgress => {
    const isBfrmp = planType === 'bfrmp';
    const stage = isBfrmp
        ? bfWorkflows[workshopStatus as BfWorkflowId]
        : faftWorkflows[workshopStatus as FaftWorkflowId];
    const workflowIds = (
        isBfrmp ? bfWorkflowIds : faftWorkflowIds
    ) as readonly string[];
    const workflows = isBfrmp ? bfWorkflows : faftWorkflows;
    const isNotStarted = workshopStatus.endsWith('_not_started');

    // stageInWorkshop: position within the active workshop's stage list
    // (excluding `_not_started`, which is logically pre-workshop).
    const stagesInThisWorkshop = workflowIds.filter((wId) => {
        if (wId.endsWith('_not_started')) return false;

        return (workflows as any)[wId].workshopId === stage.workshopId;
    });
    const stagesInWorkshop = stagesInThisWorkshop.length;
    const stageIndex = stagesInThisWorkshop.indexOf(workshopStatus);
    const stageInWorkshop = isNotStarted || stageIndex < 0 ? 0 : stageIndex + 1;

    const id = stage.id;
    // Match Figma's actual render logic: collapse to three buckets only.
    //   finalised: final workshop reached (approved or BMC-endorsed) → burgundy
    //   closed:    not started → grey
    //   available: everything else (in progress) → green
    const finalisedIds: string[] = [
        'bf_workshop_4_approved',
        'bf_workshop_4_completed',
        'faft_workshop_4_approved',
        'faft_workshop_4_completed',
    ];
    let statusType: BmcStatusType;
    if (finalisedIds.includes(id)) {
        statusType = 'finalised';
    } else if (id.endsWith('_not_started')) {
        statusType = 'closed';
    } else {
        statusType = 'available';
    }

    return {
        stageInWorkshop,
        stagesInWorkshop,
        isNotStarted,
        statusType,
        statusLabel: stage.status,
    };
};
